import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { acceptTerms } from '@/api/profile';
import { useDeleteAccount } from '@/app/(client)/profile/-hooks/useDeleteAccount';
import { useTranslator } from '@/hooks/useTranslator';
import { isImpersonating } from '@/lib/utils/managementAccess';
import { logout } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

interface UseTermsOfUseGate {
  /** Show the gate only for a signed-in, non-impersonated user who has not
   *  accepted the current terms. */
  visible: boolean;
  isAgreeing: boolean;
  agree: () => Promise<void>;
  declineConfirmOpen: boolean;
  openDeclineConfirm: () => void;
  closeDeclineConfirm: () => void;
  confirmDecline: () => Promise<void>;
  deleteConfirmOpen: boolean;
  deleteConfirmText: string;
  setDeleteConfirmText: (value: string) => void;
  isDeletePending: boolean;
  openDeleteConfirm: () => void;
  closeDeleteConfirm: () => void;
  confirmDelete: () => Promise<void>;
}

/**
 * Drives the app-level Terms of Use gate — the v2 equivalent of the old
 * dashboard's `/terms-of-use` redirect (`authorized-page.jsx`'s `needsTerms`).
 *
 * Structured like `useIntercomConsentBanner`: session read, a derived
 * `visible`, and the actions the presentation layer wires to buttons.
 */
export function useTermsOfUseGate(): UseTermsOfUseGate {
  const { getTranslation: t } = useTranslator({ page: 'termsOfUse' });
  // The delete-account confirmation reuses the profile page's own copy (see
  // `TermsDeleteAccountDialog`) — including its failure toast, so the two
  // surfaces never drift into two different "couldn't delete" messages.
  const { getTranslation: tProfile } = useTranslator({ page: 'profile' });
  const user = useSessionStore(s => s.user);
  const agreeMutation = useMutation({ mutationFn: acceptTerms });
  const { mutateAsync: deleteAccount, isPending: isDeletePending } =
    useDeleteAccount();

  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Never while impersonating: the operator's clicks would drive an action
  // (logout, account deletion) on the customer's behalf, based on a prompt
  // aimed at the customer. Same precedent as `useIntercomConsentBanner`. Also
  // defense-in-depth — the admin impersonation picker already excludes users
  // who haven't accepted terms, so this path should be unreachable in
  // practice.
  const visible =
    Boolean(user) &&
    user?.hasAgreedWithTerms === false &&
    !isImpersonating(user);

  async function agree() {
    try {
      await agreeMutation.mutateAsync();
    } catch {
      toast.error(t('agreeError'));
    }
    // Optimistic regardless of outcome — mirrors the old frontend's
    // `meReceived({ ...me, hasAgreedWithTerms: true })`. A transient PATCH
    // failure must not strand the user behind this non-dismissable dialog
    // with no way out but Decline or Delete Account.
    if (user)
      useSessionStore.getState().setUser({ ...user, hasAgreedWithTerms: true });
  }

  function openDeclineConfirm() {
    setDeclineConfirmOpen(true);
  }
  function closeDeclineConfirm() {
    setDeclineConfirmOpen(false);
  }
  async function confirmDecline() {
    toast.success(t('declinedToast'));
    await logout();
  }

  function openDeleteConfirm() {
    if (isImpersonating(user)) {
      toast.error(t('deleteImpersonatingError'));
      return;
    }
    setDeleteConfirmOpen(true);
  }
  function closeDeleteConfirm() {
    setDeleteConfirmOpen(false);
    setDeleteConfirmText('');
  }
  async function confirmDelete() {
    try {
      await deleteAccount();
      // Account is gone — reuse the shared logout so the teardown (and the
      // landing page) is identical to signing out normally.
      await logout();
    } catch {
      toast.error(tProfile('settings.deleteAccount.error'));
    }
  }

  return {
    visible,
    isAgreeing: agreeMutation.isPending,
    agree,
    declineConfirmOpen,
    openDeclineConfirm,
    closeDeclineConfirm,
    confirmDecline,
    deleteConfirmOpen,
    deleteConfirmText,
    setDeleteConfirmText,
    isDeletePending,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,
  };
}
